import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../config/api';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Mode = 'login' | 'register' | 'forgot-password';
type LoginTab = 'password' | 'code';

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');
  const [loginTab, setLoginTab] = useState<LoginTab>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [registerVerificationCode, setRegisterVerificationCode] = useState('');
  const [loginVerificationCode, setLoginVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [registerCountdown, setRegisterCountdown] = useState(0);
  const [loginCodeCountdown, setLoginCodeCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const [registerCodeSent, setRegisterCodeSent] = useState(false);
  const [loginCodeSent, setLoginCodeSent] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setMode('login');
      setLoginTab('password');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setVerificationCode('');
      setRegisterVerificationCode('');
      setLoginVerificationCode('');
      setNewPassword('');
      setConfirmNewPassword('');
      setError('');
      setShowPassword(false);
      setShowConfirmPassword(false);
      setShowNewPassword(false);
      setShowConfirmNewPassword(false);
      setCountdown(0);
      setRegisterCountdown(0);
      setLoginCodeCountdown(0);
      setCodeSent(false);
      setRegisterCodeSent(false);
      setLoginCodeSent(false);
    }
  }, [isOpen]);

  // 倒计时效果
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (registerCountdown > 0) {
      const timer = setTimeout(() => setRegisterCountdown(registerCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [registerCountdown]);

  useEffect(() => {
    if (loginCodeCountdown > 0) {
      const timer = setTimeout(() => setLoginCodeCountdown(loginCodeCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [loginCodeCountdown]);

  const handleSendLoginVerificationCode = async () => {
    setError('');

    if (!email) {
      setError('请输入邮箱地址');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('请输入有效的邮箱地址');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await apiRequest<{
        code: number;
        msg: string;
        [key: string]: any;
      }>('send/sms', {
        method: 'POST',
        body: {
          email,
          scene: 'login',
        },
      });

      console.log('发送登录验证码接口返回:', response);

      if (response.code === 0) {
        setIsLoading(false);
        setLoginCodeCountdown(60);
        setLoginCodeSent(true);
      } else {
        setIsLoading(false);
        setError(response.msg || '发送验证码失败，请稍后重试');
      }
    } catch (error: any) {
      setIsLoading(false);
      setError(error.message || '网络错误，请稍后重试');
      console.error('发送登录验证码错误:', error);
    }
  };

  const handleCodeLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !loginVerificationCode) {
      setError('请填写完整信息');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('请输入有效的邮箱地址');
      return;
    }

    if (loginVerificationCode.length !== 6) {
      setError('验证码必须是6位数字');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await apiRequest<{
        code: number;
        msg: string;
        data?: {
          token?: string;
          expires_in?: number;
          [key: string]: any;
        };
        [key: string]: any;
      }>('login/code', {
        method: 'POST',
        body: {
          email,
          code: loginVerificationCode,
        },
      });

      console.log('验证码登录接口返回:', response);

      if (response.code === 0) {
        console.log('验证码登录成功，准备保存 token');
        
        const token = response.data?.token;
        const expiresIn = response.data?.expires_in;
        
        if (token) {
          localStorage.setItem('authToken', token);
          
          if (expiresIn) {
            const expiresAt = Date.now() + expiresIn * 1000;
            localStorage.setItem('authTokenExpiresAt', expiresAt.toString());
          }
        }
        
        window.dispatchEvent(new Event('authStatusChanged'));
        
        setIsLoading(false);
        onClose();
        navigate('/user-center');
      } else {
        setIsLoading(false);
        setError(response.msg || '登录失败，请稍后重试');
      }
    } catch (error: any) {
      setIsLoading(false);
      setError(error.message || '网络错误，请稍后重试');
      console.error('验证码登录错误:', error);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('请填写完整信息');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await apiRequest<{
        code: number;
        msg: string;
        data?: {
          token?: string;
          expires_in?: number;
          [key: string]: any;
        };
        [key: string]: any;
      }>('login', {
        method: 'POST',
        body: {
          email,
          password,
        },
      });

      console.log('登录接口返回:', response);

      // 检查返回的 code 字段，0 表示成功
      if (response.code === 0) {
        // 登录成功
        console.log('登录成功，准备保存 token');
        
        // token 和 expires_in 在 data 对象中
        const token = response.data?.token;
        const expiresIn = response.data?.expires_in;
        
        console.log('token 值:', token);
        console.log('expires_in 值:', expiresIn);
        
        // 保存 token 和过期时间到 localStorage
        if (token) {
          localStorage.setItem('authToken', token);
          console.log('已保存 authToken:', localStorage.getItem('authToken'));
          
          // 保存过期时间（当前时间戳 + expires_in 秒数）
          if (expiresIn) {
            const expiresAt = Date.now() + expiresIn * 1000;
            localStorage.setItem('authTokenExpiresAt', expiresAt.toString());
            console.log('已保存 authTokenExpiresAt:', localStorage.getItem('authTokenExpiresAt'));
            console.log('过期时间戳:', expiresAt);
            console.log('当前时间戳:', Date.now());
          } else {
            console.warn('警告: expires_in 不存在');
          }
        } else {
          console.warn('警告: token 不存在');
        }
        
        // 触发自定义事件通知登录状态变化
        window.dispatchEvent(new Event('authStatusChanged'));
        console.log('已触发 authStatusChanged 事件');
        
        setIsLoading(false);
        onClose();
        navigate('/user-center');
      } else {
        // 登录失败，显示错误信息
        setIsLoading(false);
        setError(response.msg || '登录失败，请稍后重试');
      }
    } catch (error: any) {
      setIsLoading(false);
      setError(error.message || '网络错误，请稍后重试');
      console.error('登录错误:', error);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !registerVerificationCode || !password || !confirmPassword) {
      setError('请填写完整信息');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('请输入有效的邮箱地址');
      return;
    }

    if (registerVerificationCode.length !== 6) {
      setError('验证码必须是6位数字');
      return;
    }

    if (password.length < 6) {
      setError('密码长度至少6位');
      return;
    }

    if (password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await apiRequest<{
        code: number;
        msg: string;
        data?: {
          token?: string;
          expires_in?: number;
          [key: string]: any;
        };
        [key: string]: any;
      }>('register', {
        method: 'POST',
        body: {
          email,
          code: registerVerificationCode,
          password,
          confirm_password: confirmPassword,
        },
      });

      console.log('注册接口返回:', response);

      // 检查返回的 code 字段，0 表示成功
      if (response.code === 0) {
        // 注册成功
        console.log('注册成功，准备保存 token');
        
        // token 和 expires_in 在 data 对象中
        const token = response.data?.token;
        const expiresIn = response.data?.expires_in;
        
        console.log('token 值:', token);
        console.log('expires_in 值:', expiresIn);
        
        // 保存 token 和过期时间到 localStorage
        if (token) {
          localStorage.setItem('authToken', token);
          console.log('已保存 authToken:', localStorage.getItem('authToken'));
          
          // 保存过期时间（当前时间戳 + expires_in 秒数）
          if (expiresIn) {
            const expiresAt = Date.now() + expiresIn * 1000;
            localStorage.setItem('authTokenExpiresAt', expiresAt.toString());
            console.log('已保存 authTokenExpiresAt:', localStorage.getItem('authTokenExpiresAt'));
            console.log('过期时间戳:', expiresAt);
            console.log('当前时间戳:', Date.now());
          } else {
            console.warn('警告: expires_in 不存在');
          }
        } else {
          console.warn('警告: token 不存在');
        }
        
        // 触发自定义事件通知登录状态变化
        window.dispatchEvent(new Event('authStatusChanged'));
        console.log('已触发 authStatusChanged 事件');
        
        setIsLoading(false);
        onClose();
        navigate('/user-center');
      } else {
        // 注册失败，显示错误信息
        setIsLoading(false);
        setError(response.msg || '注册失败，请稍后重试');
      }
    } catch (error: any) {
      setIsLoading(false);
      setError(error.message || '网络错误，请稍后重试');
      console.error('注册错误:', error);
    }
  };

  const handleSendVerificationCode = async () => {
    setError('');

    if (!email) {
      setError('请输入邮箱地址');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('请输入有效的邮箱地址');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await apiRequest<{
        code: number;
        msg: string;
        [key: string]: any;
      }>('send/sms', {
        method: 'POST',
        body: {
          email,
          scene: 'forgot',
        },
      });

      console.log('发送验证码接口返回:', response);

      if (response.code === 0) {
        // 发送成功
        setIsLoading(false);
        setCountdown(60);
        setCodeSent(true);
      } else {
        // 发送失败
        setIsLoading(false);
        setError(response.msg || '发送验证码失败，请稍后重试');
      }
    } catch (error: any) {
      setIsLoading(false);
      setError(error.message || '网络错误，请稍后重试');
      console.error('发送验证码错误:', error);
    }
  };

  const handleSendRegisterVerificationCode = async () => {
    setError('');

    if (!email) {
      setError('请输入邮箱地址');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('请输入有效的邮箱地址');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await apiRequest<{
        code: number;
        msg: string;
        [key: string]: any;
      }>('send/sms', {
        method: 'POST',
        body: {
          email,
          scene: 'register',
        },
      });

      console.log('发送注册验证码接口返回:', response);

      if (response.code === 0) {
        // 发送成功
        setIsLoading(false);
        setRegisterCountdown(60);
        setRegisterCodeSent(true);
      } else {
        // 发送失败
        setIsLoading(false);
        setError(response.msg || '发送验证码失败，请稍后重试');
      }
    } catch (error: any) {
      setIsLoading(false);
      setError(error.message || '网络错误，请稍后重试');
      console.error('发送注册验证码错误:', error);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !verificationCode || !newPassword || !confirmNewPassword) {
      setError('请填写完整信息');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('请输入有效的邮箱地址');
      return;
    }

    if (verificationCode.length !== 6) {
      setError('验证码必须是6位数字');
      return;
    }

    if (newPassword.length < 6) {
      setError('密码长度至少6位');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await apiRequest<{
        code: number;
        msg: string;
        [key: string]: any;
      }>('forgot', {
        method: 'POST',
        body: {
          email,
          code: verificationCode,
          password: newPassword,
          confirm_password: confirmNewPassword,
        },
      });

      console.log('重置密码接口返回:', response);

      if (response.code === 0) {
        // 重置成功
        setIsLoading(false);
        onClose();
        
        // 跳转到首页
        navigate('/');
      } else {
        // 重置失败
        setIsLoading(false);
        setError(response.msg || '重置密码失败，请稍后重试');
      }
    } catch (error: any) {
      setIsLoading(false);
      setError(error.message || '网络错误，请稍后重试');
      console.error('重置密码错误:', error);
    }
  };

  if (!isOpen) return null;

  const getModalTitle = () => {
    if (mode === 'login') return '登录';
    if (mode === 'register') return '注册';
    if (mode === 'forgot-password') return '重置密码';
    return '';
  };

  const getModalDescription = () => {
    if (mode === 'login') return '欢迎回来，请登录您的账户';
    if (mode === 'register') return '创建新账户，开始使用RightChain';
    if (mode === 'forgot-password') return '请填写以下信息重置您的密码';
    return '';
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-neutral-text hover:text-neutral-dark hover:bg-neutral-bg rounded-lg transition-all cursor-pointer"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        {/* Back Button for forgot password */}
        {mode === 'forgot-password' && (
          <button
            onClick={() => setMode('login')}
            className="absolute top-4 left-4 w-10 h-10 flex items-center justify-center text-neutral-text hover:text-neutral-dark hover:bg-neutral-bg rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
        )}

        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src="https://static.readdy.ai/image/66a2b9cfb9b417b2846085a20d5b8a97/39f48c36d78167ba77a6bec1ef6513fc.png" 
            alt="RightChain" 
            className="h-16 w-16 mx-auto mb-4 object-contain"
          />
          <h2 className="text-2xl font-bold text-neutral-dark">
            {getModalTitle()}
          </h2>
          <p className="text-neutral-text mt-2">
            {getModalDescription()}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
            <span className="text-sm text-red-600">{error}</span>
          </div>
        )}

        {/* Login Form */}
        {mode === 'login' && (
          <>
            {/* Login Tab Switcher */}
            <div className="flex mb-6 bg-neutral-bg rounded-full p-1">
              <button
                type="button"
                onClick={() => {
                  setLoginTab('password');
                  setError('');
                }}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  loginTab === 'password'
                    ? 'bg-white text-neutral-dark shadow-sm'
                    : 'text-neutral-text hover:text-neutral-dark'
                }`}
              >
                密码登录
              </button>
              <button
                type="button"
                onClick={() => {
                  setLoginTab('code');
                  setError('');
                }}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  loginTab === 'code'
                    ? 'bg-white text-neutral-dark shadow-sm'
                    : 'text-neutral-text hover:text-neutral-dark'
                }`}
              >
                验证码登录
              </button>
            </div>

            {/* Password Login Form */}
            {loginTab === 'password' && (
              <form onSubmit={handleLogin}>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-neutral-dark mb-2">
                    邮箱地址
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-neutral-dark mb-2">
                    密码
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="请输入密码"
                      className="w-full px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-text hover:text-neutral-dark cursor-pointer"
                    >
                      <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-xl`}></i>
                    </button>
                  </div>
                </div>

                <div className="mb-6 text-right">
                  <button
                    type="button"
                    onClick={() => setMode('forgot-password')}
                    className="text-sm text-primary hover:text-primary-dark cursor-pointer"
                  >
                    忘记密码？
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-6 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      登录中...
                    </>
                  ) : (
                    <>登录</>
                  )}
                </button>

                {/* Switch to Register */}
                <div className="mt-6 text-center">
                  <span className="text-sm text-neutral-text">还没有账户？</span>
                  <button
                    type="button"
                    onClick={() => setMode('register')}
                    className="ml-2 text-sm text-primary hover:text-primary-dark font-semibold cursor-pointer"
                  >
                    立即注册
                  </button>
                </div>

                {/* Features Preview */}
                <div className="mt-8 pt-6 border-t border-neutral-bg">
                  <div className="text-sm font-semibold text-neutral-dark mb-3">登录后可以：</div>
                  <div className="space-y-2">
                    {[
                      { icon: 'ri-file-add-line', text: '登记数字权益到区块链' },
                      { icon: 'ri-search-line', text: '查询和验证NFT真实性' },
                      { icon: 'ri-wallet-3-line', text: '管理托管的数字资产' },
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center gap-3 text-sm text-neutral-text">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className={`${feature.icon} text-primary`}></i>
                        </div>
                        <span>{feature.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </form>
            )}

            {/* Code Login Form */}
            {loginTab === 'code' && (
              <form onSubmit={handleCodeLogin}>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-neutral-dark mb-2">
                    邮箱地址
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-neutral-dark mb-2">
                    验证码
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={loginVerificationCode}
                      onChange={(e) => setLoginVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="请输入6位验证码"
                      className="flex-1 px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      maxLength={6}
                      required
                    />
                    <button
                      type="button"
                      onClick={handleSendLoginVerificationCode}
                      disabled={isLoading || loginCodeCountdown > 0 || !email}
                      className="px-4 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {isLoading ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : loginCodeCountdown > 0 ? (
                        `${loginCodeCountdown}s`
                      ) : (
                        '获取验证码'
                      )}
                    </button>
                  </div>
                  {loginCodeSent && (
                    <p className="text-xs text-green-600 mt-1">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      验证码已发送到您的邮箱
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-6 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      登录中...
                    </>
                  ) : (
                    <>登录</>
                  )}
                </button>

                {/* Switch to Register */}
                <div className="mt-6 text-center">
                  <span className="text-sm text-neutral-text">还没有账户？</span>
                  <button
                    type="button"
                    onClick={() => setMode('register')}
                    className="ml-2 text-sm text-primary hover:text-primary-dark font-semibold cursor-pointer"
                  >
                    立即注册
                  </button>
                </div>

                {/* Features Preview */}
                <div className="mt-8 pt-6 border-t border-neutral-bg">
                  <div className="text-sm font-semibold text-neutral-dark mb-3">登录后可以：</div>
                  <div className="space-y-2">
                    {[
                      { icon: 'ri-file-add-line', text: '登记数字权益到区块链' },
                      { icon: 'ri-search-line', text: '查询和验证NFT真实性' },
                      { icon: 'ri-wallet-3-line', text: '管理托管的数字资产' },
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center gap-3 text-sm text-neutral-text">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className={`${feature.icon} text-primary`}></i>
                        </div>
                        <span>{feature.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </form>
            )}
          </>
        )}

        {/* Register Form */}
        {mode === 'register' && (
          <form onSubmit={handleRegister}>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-neutral-dark mb-2">
                邮箱地址
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                required
              />
            </div>

            {/* 验证码输入框和发送按钮 */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-neutral-dark mb-2">
                验证码
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={registerVerificationCode}
                  onChange={(e) => setRegisterVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="请输入6位验证码"
                  className="flex-1 px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  maxLength={6}
                  required
                />
                <button
                  type="button"
                  onClick={handleSendRegisterVerificationCode}
                  disabled={isLoading || registerCountdown > 0 || !email}
                  className="px-4 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isLoading ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : registerCountdown > 0 ? (
                    `${registerCountdown}s`
                  ) : (
                    '获取验证码'
                  )}
                </button>
              </div>
              {registerCodeSent && (
                <p className="text-xs text-green-600 mt-1">
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  验证码已发送到您的邮箱
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-neutral-dark mb-2">
                密码
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="至少6位密码"
                  className="w-full px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-text hover:text-neutral-dark cursor-pointer"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-xl`}></i>
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-neutral-dark mb-2">
                确认密码
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="再次输入密码"
                  className="w-full px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-text hover:text-neutral-dark cursor-pointer"
                >
                  <i className={`${showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-xl`}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  注册中...
                </>
              ) : (
                <>注册</>
              )}
            </button>

            {/* Switch to Login */}
            <div className="mt-6 text-center">
              <span className="text-sm text-neutral-text">已有账户？</span>
              <button
                type="button"
                onClick={() => setMode('login')}
                className="ml-2 text-sm text-primary hover:text-primary-dark font-semibold cursor-pointer"
              >
                立即登录
              </button>
            </div>

            {/* Terms */}
            <div className="mt-6 p-3 bg-neutral-bg/50 rounded-lg">
              <p className="text-xs text-neutral-text text-center">
                注册即表示您同意我们的
                <a href="#" className="text-primary hover:text-primary-dark cursor-pointer">服务条款</a>
                和
                <a href="#" className="text-primary hover:text-primary-dark cursor-pointer">隐私政策</a>
              </p>
            </div>
          </form>
        )}

        {/* Forgot Password Form */}
        {mode === 'forgot-password' && (
          <form onSubmit={handleResetPassword}>
            {/* Email Input */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-neutral-dark mb-2">
                邮箱地址
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="请输入注册时使用的邮箱"
                className="w-full px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                required
              />
            </div>

            {/* Verification Code Input with Send Button */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-neutral-dark mb-2">
                验证码
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="请输入6位验证码"
                  className="flex-1 px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  maxLength={6}
                  required
                />
                <button
                  type="button"
                  onClick={handleSendVerificationCode}
                  disabled={isLoading || countdown > 0 || !email}
                  className="px-4 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isLoading ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : countdown > 0 ? (
                    `${countdown}s`
                  ) : (
                    '发送验证码'
                  )}
                </button>
              </div>
              {codeSent && (
                <p className="text-xs text-green-600 mt-1">
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  验证码已发送到您的邮箱
                </p>
              )}
            </div>

            {/* New Password */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-neutral-dark mb-2">
                新密码
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="请输入新密码（至少6位）"
                  className="w-full px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-text hover:text-neutral-dark cursor-pointer"
                >
                  <i className={`${showNewPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-xl`}></i>
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-neutral-dark mb-2">
                确认新密码
              </label>
              <div className="relative">
                <input
                  type={showConfirmNewPassword ? 'text' : 'password'}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="请再次输入新密码"
                  className="w-full px-4 py-3 border border-neutral-bg rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-text hover:text-neutral-dark cursor-pointer"
                >
                  <i className={`${showConfirmNewPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-xl`}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  重置中...
                </>
              ) : (
                <>重置密码</>
              )}
            </button>

            {/* Back to Login */}
            <div className="mt-6 text-center">
              <span className="text-sm text-neutral-text">想起密码了？</span>
              <button
                type="button"
                onClick={() => setMode('login')}
                className="ml-2 text-sm text-primary hover:text-primary-dark font-semibold cursor-pointer"
              >
                立即登录
              </button>
            </div>

            {/* Tips */}
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <i className="ri-shield-check-line text-green-500 mt-0.5"></i>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-green-800 mb-1">安全提示</h4>
                  <p className="text-xs text-green-700">
                    密码重置后将自动返回登录页面，请使用新密码登录
                  </p>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
